import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { AlertTriangle, Download, ExternalLink, FileText, Loader2, Maximize2, Minus, Plus, RotateCcw, Sparkles, Trash2, Undo2, X } from "lucide-react";
import { PdfPreview } from "@/components/pdf-preview";

type LinkedExpense = { id: number; description: string | null; amount: string; date: string; voidedAt: string | null };
type LinkedReno = { id: number; itemName: string; budget: string | null; voidedAt: string | null };

type DocSummary = {
  id: number;
  propertyId: number;
  fileName: string;
  mimeType: string;
  category: string;
  vendorId: number | null;
  extractedNumber: string | null;
  extractedData: Record<string, any> | null;
  uploadedAt: string;
  voidedAt: string | null;
  linkedExpenses: LinkedExpense[];
  linkedRenovationItems: LinkedReno[];
};

type ReExtractResp = {
  documentId: number;
  mode: "invoice" | "expense" | "bid";
  extracted: Record<string, any>;
  confidence: number;
  warnings: string[];
};

interface Props {
  open: boolean;
  onClose: () => void;
  documentId: number;
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function baseMime(mime: string) {
  return mime.split(";")[0].trim().toLowerCase();
}

function isImageMime(mime: string) {
  return /^image\/(jpeg|jpg|png|webp|gif)$/.test(baseMime(mime));
}

function isPdfMime(mime: string) {
  return baseMime(mime) === "application/pdf";
}

function FullscreenViewer({
  fileUrl,
  fileName,
  mode,
  onClose,
}: {
  fileUrl: string;
  fileName: string;
  mode: "image" | "pdf";
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if (mode === "image") {
        if (e.key === "+" || e.key === "=") setZoom(z => Math.min(8, +(z + 0.25).toFixed(2)));
        else if (e.key === "-") setZoom(z => Math.max(0.25, +(z - 0.25).toFixed(2)));
        else if (e.key === "0") setZoom(1);
      }
    }
    window.addEventListener("keydown", onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [mode, onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={`${fileName} preview`}
      data-testid="overlay-document-fullscreen"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-black/60 text-white">
        <div className="text-sm truncate min-w-0" data-testid="text-fullscreen-filename">{fileName}</div>
        <div className="flex items-center gap-1.5 shrink-0">
          {mode === "image" && (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="text-white hover:bg-white/10 hover:text-white h-8 w-8"
                onClick={() => setZoom(z => Math.max(0.25, +(z - 0.25).toFixed(2)))}
                aria-label="Zoom out"
                data-testid="button-zoom-out"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="text-xs tabular-nums w-12 text-center" data-testid="text-zoom-level">{Math.round(zoom * 100)}%</span>
              <Button
                size="icon"
                variant="ghost"
                className="text-white hover:bg-white/10 hover:text-white h-8 w-8"
                onClick={() => setZoom(z => Math.min(8, +(z + 0.25).toFixed(2)))}
                aria-label="Zoom in"
                data-testid="button-zoom-in"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/10 hover:text-white h-8"
                onClick={() => setZoom(1)}
                data-testid="button-zoom-reset"
              >
                Reset
              </Button>
            </>
          )}
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white px-2 py-1"
            data-testid="link-open-fullscreen-file"
          >
            Open in new tab <ExternalLink className="h-3 w-3" />
          </a>
          <Button
            size="icon"
            variant="ghost"
            className="text-white hover:bg-white/10 hover:text-white h-8 w-8"
            onClick={onClose}
            aria-label="Close"
            data-testid="button-close-fullscreen"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
      <div
        className="flex-1 overflow-auto flex items-start justify-center p-4"
        onClick={(e) => { if (mode === "image" && e.target === e.currentTarget) onClose(); }}
      >
        {mode === "image" ? (
          <img
            src={fileUrl}
            alt={fileName}
            style={{ transform: `scale(${zoom})`, transformOrigin: "center top" }}
            className="max-w-none transition-transform duration-100 select-none"
            draggable={false}
            data-testid="img-fullscreen-preview"
          />
        ) : (
          <div className="w-full h-full bg-white rounded">
            <PdfPreview
              file={fileUrl}
              openInNewTabHref={fileUrl}
              testIdPrefix="pdf-fullscreen-preview"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentPreview({ documentId, mimeType, fileName }: { documentId: number; mimeType: string; fileName: string }) {
  const [imgError, setImgError] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const fileUrl = `/api/app/property-documents/${documentId}/file`;

  const fallback = (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 flex items-center gap-3" data-testid="block-document-preview">
      <FileText className="h-8 w-8 text-gray-400 shrink-0" />
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-700 truncate">{fileName}</div>
        <div className="text-xs text-gray-500">{mimeType || "Unknown file type"}</div>
      </div>
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
        data-testid="link-open-file"
      >
        Open file <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );

  if (isImageMime(mimeType) && !imgError) {
    return (
      <>
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 relative group" data-testid="block-document-preview">
          {imgLoading && (
            <div className="flex items-center justify-center h-24 text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />Loading preview…
            </div>
          )}
          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className={`w-full block ${imgLoading ? "hidden" : ""} cursor-zoom-in`}
            aria-label="Open full-screen preview"
            data-testid="button-open-fullscreen-preview"
          >
            <img
              src={fileUrl}
              alt={fileName}
              className="w-full max-h-64 object-contain"
              onLoad={() => setImgLoading(false)}
              onError={() => { setImgLoading(false); setImgError(true); }}
              data-testid="img-document-preview"
            />
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs bg-black/60 text-white rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="h-3 w-3" />Full screen
            </span>
          </button>
        </div>
        {fullscreen && (
          <FullscreenViewer fileUrl={fileUrl} fileName={fileName} mode="image" onClose={() => setFullscreen(false)} />
        )}
      </>
    );
  }

  if (imgError) return fallback;

  if (isPdfMime(mimeType) && !pdfError) {
    return (
      <>
        <div className="border border-gray-200 rounded-lg overflow-hidden relative h-72" data-testid="block-document-preview">
          <PdfPreview
            file={fileUrl}
            openInNewTabHref={fileUrl}
            testIdPrefix="pdf-document-preview"
          />
          <Button
            size="sm"
            variant="secondary"
            className="absolute top-2 right-2 h-7 px-2 text-xs shadow"
            onClick={() => setFullscreen(true)}
            data-testid="button-open-fullscreen-preview"
          >
            <Maximize2 className="h-3.5 w-3.5 mr-1" />Full screen
          </Button>
        </div>
        {fullscreen && (
          <FullscreenViewer fileUrl={fileUrl} fileName={fileName} mode="pdf" onClose={() => setFullscreen(false)} />
        )}
      </>
    );
  }

  return fallback;
}

export function ManageDocumentDialog({ open, onClose, documentId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmingVoid, setConfirmingVoid] = useState(false);
  const [reExtractResult, setReExtractResult] = useState<ReExtractResp | null>(null);

  const docQuery = useQuery<DocSummary>({
    queryKey: ["/api/app/property-documents", documentId],
    enabled: open,
  });
  const doc = docQuery.data;

  function invalidateRelated() {
    queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/property-documents", documentId] });
    if (doc?.propertyId) {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", doc.propertyId, "renovation-items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", doc.propertyId, "documents"] });
    }
  }

  const reExtractMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/property-documents/${documentId}/re-extract`, {});
      return res.json() as Promise<ReExtractResp>;
    },
    onSuccess: (r) => {
      setReExtractResult(r);
      toast({ title: "Re-extraction complete", description: `Confidence ${Math.round(r.confidence * 100)}%. Review the new data below.` });
      docQuery.refetch();
    },
    onError: (e: any) => toast({ title: "Re-extract failed", description: parseErrorMessage(e, "Couldn't re-run extraction. Please try again."), variant: "destructive" }),
  });

  const voidMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/property-documents/${documentId}/void`, {});
      return res.json() as Promise<{ voidedExpenseIds: number[]; voidedRenovationItemIds: number[] }>;
    },
    onSuccess: (r) => {
      toast({ title: "Document voided", description: `${r.voidedExpenseIds.length} expense(s) and ${r.voidedRenovationItemIds.length} renovation item(s) hidden from totals.` });
      invalidateRelated();
      setConfirmingVoid(false);
      onClose();
    },
    onError: (e: any) => toast({ title: "Void failed", description: parseErrorMessage(e, "Couldn't void this document. Please try again."), variant: "destructive" }),
  });

  const restoreMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/property-documents/${documentId}/restore`, {});
      return res.json() as Promise<{ restoredExpenseIds: number[]; restoredRenovationItemIds: number[] }>;
    },
    onSuccess: (r) => {
      toast({ title: "Document restored", description: `${r.restoredExpenseIds.length} expense(s) and ${r.restoredRenovationItemIds.length} renovation item(s) restored.` });
      invalidateRelated();
      onClose();
    },
    onError: (e: any) => toast({ title: "Restore failed", description: parseErrorMessage(e, "Couldn't restore this document. Please try again."), variant: "destructive" }),
  });

  const isVoided = !!doc?.voidedAt;
  const linkedActiveExpenses = doc?.linkedExpenses.filter(e => !e.voidedAt) ?? [];
  const linkedActiveRenos = doc?.linkedRenovationItems.filter(i => !i.voidedAt) ?? [];

  const busy = reExtractMutation.isPending || voidMutation.isPending || restoreMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (busy) return; if (!o) { setReExtractResult(null); setConfirmingVoid(false); onClose(); } }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-manage-document"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Manage source document</DialogTitle>
        </DialogHeader>

        {docQuery.isLoading || !doc ? (
          <div className="py-12 flex items-center justify-center text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading document…
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-gray-900" data-testid="text-document-filename">{doc.fileName}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {doc.category.replace(/_/g, " ")} · uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                    {doc.extractedNumber && <> · #{doc.extractedNumber}</>}
                  </div>
                </div>
                {isVoided && (
                  <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700" data-testid="badge-voided">
                    Voided {doc.voidedAt ? new Date(doc.voidedAt).toLocaleDateString() : ""}
                  </Badge>
                )}
              </div>
            </div>

            <DocumentPreview documentId={documentId} mimeType={doc.mimeType} fileName={doc.fileName} />

            <div>
              <div className="text-xs font-semibold text-gray-700 mb-1.5">Linked expenses ({linkedActiveExpenses.length}{doc.linkedExpenses.length !== linkedActiveExpenses.length ? ` active / ${doc.linkedExpenses.length} total` : ""})</div>
              {doc.linkedExpenses.length === 0 ? (
                <div className="text-xs text-gray-400 italic">No expenses linked.</div>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto overflow-x-hidden" data-testid="list-linked-expenses">
                  {doc.linkedExpenses.map(e => (
                    <div key={e.id} className={`flex items-center gap-2 text-xs px-2 py-1 rounded min-w-0 overflow-hidden ${e.voidedAt ? "bg-red-50 text-red-700 line-through" : "bg-white border border-gray-100 text-gray-700"}`} data-testid={`item-linked-expense-${e.id}`}>
                      <span className="truncate min-w-0 flex-1">{e.date} · {e.description || "—"}</span>
                      <span className="font-medium shrink-0">{fmtCurrency(parseFloat(e.amount || "0"))}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-700 mb-1.5">Linked renovation items ({linkedActiveRenos.length}{doc.linkedRenovationItems.length !== linkedActiveRenos.length ? ` active / ${doc.linkedRenovationItems.length} total` : ""})</div>
              {doc.linkedRenovationItems.length === 0 ? (
                <div className="text-xs text-gray-400 italic">No renovation items linked.</div>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto overflow-x-hidden" data-testid="list-linked-renovation-items">
                  {doc.linkedRenovationItems.map(i => (
                    <div key={i.id} className={`flex items-center gap-2 text-xs px-2 py-1 rounded min-w-0 overflow-hidden ${i.voidedAt ? "bg-red-50 text-red-700 line-through" : "bg-white border border-gray-100 text-gray-700"}`} data-testid={`item-linked-renovation-${i.id}`}>
                      <span className="truncate min-w-0 flex-1">{i.itemName}</span>
                      <span className="font-medium shrink-0">{i.budget ? fmtCurrency(parseFloat(i.budget)) : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {reExtractResult && (
              <div className="border border-blue-200 bg-blue-50 rounded p-3 text-sm" data-testid="block-reextract-result">
                <div className="flex items-center gap-1.5 font-medium text-blue-900 mb-1">
                  <Sparkles className="h-4 w-4" />Fresh extraction (confidence {Math.round(reExtractResult.confidence * 100)}%)
                </div>
                {reExtractResult.warnings.length > 0 && (
                  <ul className="text-xs text-blue-800 list-disc pl-4 mb-1.5">
                    {reExtractResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}
                <pre className="text-[11px] bg-white border border-blue-100 rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap" data-testid="text-reextract-json">
                  {JSON.stringify(reExtractResult.extracted, null, 2)}
                </pre>
                <div className="text-xs text-blue-800 mt-1.5">
                  The stored data has been updated. To replace the current expenses or renovation budgets, void this document and re-upload the file from the financials or renovations page.
                </div>
              </div>
            )}

            {confirmingVoid && !isVoided && (
              <div className="border border-red-300 bg-red-50 rounded p-3 text-sm" data-testid="block-confirm-void">
                <div className="flex items-center gap-1.5 font-medium text-red-900 mb-1">
                  <AlertTriangle className="h-4 w-4" />Void this document?
                </div>
                <div className="text-xs text-red-800">
                  This will hide {linkedActiveExpenses.length} expense(s) and {linkedActiveRenos.length} renovation item(s) from totals and reports. The records are kept (soft-deleted) so you can restore them later.
                </div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="destructive" onClick={() => voidMutation.mutate()} disabled={voidMutation.isPending} data-testid="button-confirm-void">
                    {voidMutation.isPending ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Voiding…</> : "Yes, void"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmingVoid(false)} data-testid="button-cancel-void">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose} data-testid="button-close-manage">Close</Button>
          {doc && (
            <Button
              variant="outline"
              asChild
              data-testid="button-download-document"
            >
              <a
                href={`/api/app/property-documents/${documentId}/file?download=1`}
                download={doc.fileName}
              >
                <Download className="mr-1.5 h-4 w-4" />Download
              </a>
            </Button>
          )}
          {doc && !isVoided && (
            <>
              <Button
                variant="outline"
                onClick={() => reExtractMutation.mutate()}
                disabled={reExtractMutation.isPending}
                data-testid="button-re-extract"
              >
                {reExtractMutation.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Re-extracting…</> : <><RotateCcw className="mr-1.5 h-4 w-4" />Re-run extraction</>}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setConfirmingVoid(true)}
                disabled={confirmingVoid}
                data-testid="button-void-document"
              >
                <Trash2 className="mr-1.5 h-4 w-4" />Void document
              </Button>
            </>
          )}
          {doc && isVoided && (
            <Button
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
              data-testid="button-restore-document"
            >
              {restoreMutation.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Restoring…</> : <><Undo2 className="mr-1.5 h-4 w-4" />Restore document</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
